import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, MailWarning } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatZAR, formatApiError } from '../lib/format';

const PRICE = 5;

export default function PaymentMock() {
  const [sp] = useSearchParams();
  const qty = Math.max(1, Math.min(100, parseInt(sp.get('qty') || '1', 10) || 1));
  const newAccount = sp.get('newAccount') === '1';
  const total = qty * PRICE;
  const navigate = useNavigate();
  const { user, checked } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (checked && !user) {
      navigate(`/buy-ticket?qty=${qty}`, { replace: true });
    }
  }, [checked, user, qty, navigate]);

  const simulate = async () => {
    setLoading(true);
    try {
      await api.post('/tickets/purchase', { qty });
      toast.success(`Purchased ${qty} ticket${qty > 1 ? 's' : ''}`, {
        description: `Total: ${formatZAR(total)}`,
      });
      navigate('/dashboard');
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!checked || !user) return null;

  const showVerifyNotice = newAccount || !user.email_verified;

  return (
    <div data-testid="payment-mock" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div
        data-testid="payment-placeholder-banner"
        className="mb-6 rounded-xl border border-[#D4AF37]/40 bg-[#1a1400]/40 p-5 flex items-start gap-3"
      >
        <AlertTriangle className="text-[#FFD700] mt-1" size={22} />
        <div>
          <p className="font-semibold text-[#FFD700]">Payment Gateway Placeholder</p>
          <p className="text-sm text-[#C0C0C0] mt-1">
            AddPay integration coming soon. For now you can simulate a successful payment to receive your tickets.
          </p>
        </div>
      </div>

      {showVerifyNotice && (
        <div
          data-testid="verify-later-notice"
          className="mb-6 rounded-xl border border-[#D4AF37]/25 bg-[#141414] p-4 flex items-start gap-3"
        >
          <MailWarning className="text-[#D4AF37] mt-0.5" size={18} />
          <p className="text-sm text-[#C0C0C0]">
            We'll email you a verification link — <span className="text-white">verify before your first payout.</span>
          </p>
        </div>
      )}

      <div className="card-embossed p-8 sm:p-10">
        <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">Order summary</p>
        <h2 className="mt-2 font-serif-display text-3xl font-black text-white">Golden Catch Tickets</h2>

        <div className="mt-8 border-t border-[#D4AF37]/15 pt-6 space-y-3 text-sm">
          <div className="flex justify-between text-[#C0C0C0]">
            <span>Tickets</span>
            <span>{qty}</span>
          </div>
          <div className="flex justify-between text-[#C0C0C0]">
            <span>Price per ticket</span>
            <span>{formatZAR(PRICE)}</span>
          </div>
          <div className="flex justify-between text-[#C0C0C0]">
            <span>Draw</span>
            <span>Friday 6PM SAST</span>
          </div>
          <div className="border-t border-[#D4AF37]/15 pt-4 flex justify-between text-xl">
            <span className="text-white">Total</span>
            <span data-testid="payment-total" className="font-serif-display text-3xl text-gold-gradient font-black">
              {formatZAR(total)}
            </span>
          </div>
        </div>

        <div className="mt-10 flex flex-col-reverse sm:flex-row justify-end gap-3">
          <Link to="/buy-ticket" data-testid="payment-cancel" className="btn-ghost-gold text-center">
            Cancel
          </Link>
          <button
            data-testid="payment-simulate"
            disabled={loading}
            onClick={simulate}
            className="btn-gold inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            Simulate Successful Payment
          </button>
        </div>
      </div>
    </div>
  );
}
